import { DATE_RANGE_TOKENS, type SearchIntent } from "./types";

/**
 * Kept clear of the axios client on purpose: importing that pulls in Keycloak
 * and, through it, `window`, which makes the rule untestable in a node suite.
 */
/** Clamp to the documented bounds rather than trusting the model's arithmetic. */
const MIN_LIMIT = 1;
const MAX_LIMIT = 100;
const DEFAULT_LIMIT = 25;

/**
 * Normalises whatever comes back into something safe to search with.
 *
 * The response is generated text, so every field is treated as a suggestion:
 * a limit of 5000, a `date_range` of "last_week" or a missing `terms` array
 * are all recoverable, and recovering beats showing an error for a search the
 * user can see was understood correctly.
 */
export function normaliseIntent(raw: Partial<SearchIntent>): SearchIntent {
  const limit = Number(raw.limit);
  return {
    schema_version: 1,
    terms: (raw.terms ?? []).filter((t): t is string => !!t?.trim()),
    tickers: (raw.tickers ?? []).filter((t): t is string => !!t?.trim()),
    limit: Number.isFinite(limit)
      ? Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, Math.round(limit)))
      : DEFAULT_LIMIT,
    exact_match: raw.exact_match === true,
    keyword_op: raw.keyword_op === "AND" ? "AND" : "OR",
    filter_mode: raw.filter_mode === "AND" ? "AND" : "OR",
    date_range: DATE_RANGE_TOKENS.includes(
      raw.date_range as (typeof DATE_RANGE_TOKENS)[number]
    )
      ? raw.date_range!
      : "any",
    explanation: raw.explanation?.trim() || "",
  };
}
